import type {
  AppliedEventKey,
  ChatbotLogger,
  ChatEventCallback,
  ChatMessageCallback,
} from '../types';

/**
 * Subscribes to assistant/user messages.
 */
export function setOnMessage(
  callback: ChatMessageCallback,
  logger: ChatbotLogger | Console = console,
): void {
  try {
    window.applied?.setOnMessage?.(async (message) => {
      await callback(message);
    });
    logger.info?.({
      type: 'client',
      context: 'Chatbot - setOnMessage',
      message: 'onMessage handler set',
      sessionId: undefined,
    });
  } catch (err) {
    logger.error?.(
      {
        type: 'client',
        context: 'Chatbot - setOnMessage',
        message: 'Failed to set onMessage handler',
        sessionId: undefined,
      },
      { error: err },
    );
  }
}

/**
 * Subscribes to internal Applied Labs chat events.
 */
export function subscribeToEvent<T>(
  event: AppliedEventKey,
  callback: ChatEventCallback<T>,
  logger: ChatbotLogger | Console = console,
): number {
  try {
    const id = window.applied?.events?.subscribeEvent?.(event, callback);
    logger.info?.({
      type: 'client',
      context: 'Chatbot - subscribeToEvent',
      message: `Subscribed to event '${event}'`,
      sessionId: undefined,
    });
    return id as number;
  } catch (err) {
    logger.error?.(
      {
        type: 'client',
        context: 'Chatbot - subscribeToEvent',
        message: `Failed to subscribe to event '${event}'`,
        sessionId: undefined,
      },
      { error: err },
    );
    return -1;
  }
}

/**
 * Unsubscribes from internal Applied Labs chat events.
 */
export function unsubscribeFromEvent(id: number, logger: ChatbotLogger | Console = console): void {
  try {
    window.applied?.events?.unsubscribeEvent?.(id);
    logger.info?.({
      type: 'client',
      context: 'Chatbot - unsubscribeFromEvent',
      message: `Unsubscribed from event with id '${id}'`,
      sessionId: undefined,
    });
  } catch (err) {
    logger.error?.(
      {
        type: 'client',
        context: 'Chatbot - unsubscribeFromEvent',
        message: `Failed to unsubscribe from event '${id}'`,
        sessionId: undefined,
      },
      { error: err },
    );
  }
}

/**
 * Fires a custom internal event.
 */
export function fireEvent(
  event: string,
  payload?: unknown,
  logger: ChatbotLogger | Console = console,
): void {
  try {
    window.applied?.events?.fireEvent?.(event, payload);
    logger.info?.(
      {
        type: 'client',
        context: 'Chatbot - fireEvent',
        message: `Fired event '${event}'`,
        sessionId: undefined,
      },
      { payload },
    );
  } catch (err) {
    logger.error?.(
      {
        type: 'client',
        context: 'Chatbot - fireEvent',
        message: `Failed to fire event '${event}'`,
        sessionId: undefined,
      },
      { error: err },
    );
  }
}

/**
 * Tracks an analytics event.
 */
export function trackEvent(
  event: string,
  value?: unknown,
  meta?: Record<string, unknown>,
  logger: ChatbotLogger | Console = console,
): void {
  try {
    window.applied?.events?.trackEvent?.(event, value, meta);
    logger.info?.(
      {
        type: 'client',
        context: 'Chatbot - trackEvent',
        message: `Tracked event '${event}'`,
        sessionId: undefined,
      },
      { value, meta },
    );
  } catch (err) {
    logger.error?.(
      {
        type: 'client',
        context: 'Chatbot - trackEvent',
        message: `Failed to track event '${event}'`,
        sessionId: undefined,
      },
      { error: err },
    );
  }
}
